import { Injectable } from '@angular/core';
import { IAgentConfig, ITabsConfig } from '../config';
import { merge, isNil } from 'lodash';
import { AGENT_MODEL, DevicesService } from '@nrwl/common-lib';

@Injectable()
export class TestSetupAgentModelService {
  constructor(private readonly deviceService: DevicesService) {}
  //eslint-disable-next-line
  getAgentBasedConfig(agentModelConfig: any): IAgentConfig[] {
    const agentConfig: IAgentConfig[] = [];
    const defaults = agentModelConfig.agentConfig.default.setup;
    for (const agentType of Object.keys(agentModelConfig.agentConfig)) {
      //Deep merging agent model with default models
      const setupConfig = merge({}, defaults, agentModelConfig.agentConfig[agentType].setup);
      const config = this.getIndividualAgentConfig(agentType, setupConfig);
      agentConfig.push(config);
      // check version based agent model changes
      if (agentModelConfig.agentConfig[agentType].versions) {
        for (const versionValue of Object.keys(agentModelConfig.agentConfig[agentType].versions)) {
          const versionConfig = merge(
            {},
            defaults,
            agentModelConfig.agentConfig[agentType].setup,
            agentModelConfig.agentConfig[agentType].versions[versionValue].setup
          );
          const agentVersionType = agentType + '-' + versionValue;
          const config = this.getIndividualAgentConfig(agentVersionType, versionConfig);
          agentConfig.push(config);

          // check testmode based agent model changes
          if (agentModelConfig.agentConfig[agentType].modes) {
            for (const testMode of Object.keys(agentModelConfig.agentConfig[agentType].modes)) {
              const modeConfig = merge(
                {},
                versionConfig,
                agentModelConfig.agentConfig[agentType].modes[testMode].setup
              );
              const agentVersionType = agentType + '-' + versionValue + '-' + testMode;
              const config = this.getIndividualAgentConfig(agentVersionType, modeConfig);
              agentConfig.push(config);
            }
          }
        }
      }
      // check testmode based agent model changes
      if (agentModelConfig.agentConfig[agentType].modes) {
        for (const testMode of Object.keys(agentModelConfig.agentConfig[agentType].modes)) {
          const modeConfig = merge(
            {},
            defaults,
            agentModelConfig.agentConfig[agentType].setup,
            agentModelConfig.agentConfig[agentType].modes[testMode].setup
          );
          const agentVersionType = agentType + '-' + testMode;
          const config = this.getIndividualAgentConfig(agentVersionType, modeConfig);
          agentConfig.push(config);
        }
      }
    }
    return agentConfig;
  }

  //eslint-disable-next-line
  getIndividualAgentConfig(agentType: string, setupConfig: any): IAgentConfig {
    const tabsConfig: Array<ITabsConfig> = [];
    for (const fieldName of Object.keys(setupConfig)) {
      const fieldConfig = setupConfig[fieldName];
      if (fieldConfig.enabled) {
        const tabId = fieldConfig.tabId;
        const config = tabsConfig.find(config => config.tabId === tabId);
        if (config) {
          config.valuesDefault = {
            ...config.valuesDefault,
            [fieldConfig.outputField]: fieldConfig.value
          };
          config.outputs.push(fieldConfig.outputField);
          if (fieldConfig.outputOptional) {
            config.outputOptional.push(fieldConfig.outputField);
          }
          if (fieldConfig.restoreOnAgentChange) {
            config.restoreOutputs.push(fieldConfig.outputField);
          }
          if (fieldConfig.options) {
            config.options = {
              ...config.options,
              [fieldConfig.outputField]: fieldConfig.options
            };
          }
          if (fieldConfig.lookup) {
            config.lookup = {
              ...config.lookup,
              [fieldConfig.outputField]: fieldConfig.lookup
            };
          }
        } else {
          const object = {
            tabId: tabId,
            valuesDefault: { [fieldConfig.outputField]: fieldConfig.value },
            outputs: !fieldConfig.outputOptional ? [fieldConfig.outputField] : [],
            outputOptional: fieldConfig.outputOptional ? [fieldConfig.outputField] : [],
            restoreOutputs: fieldConfig.restoreOnAgentChange ? [fieldConfig.outputField] : [],
            options: fieldConfig.options ? { [fieldConfig.outputField]: fieldConfig.options } : {},
            lookup: fieldConfig.lookup ? { [fieldConfig.lookup]: fieldConfig.options } : null
          };
          tabsConfig.push(object);
        }
      }
    }
    return { agentType: agentType, tabsConfig: tabsConfig };
  }

  // Override the default model value with the required params
  //eslint-disable-next-line
  restoreDefaultValueWithOutput(agentModelConfig: any, parsedOutputParams: any): any {
    const defaultsSetup = agentModelConfig.agentConfig.default.setup;
    const agentType = this.deviceService.isMtsAgentModel(parsedOutputParams['agentType'])
      ? AGENT_MODEL.MTS
      : parsedOutputParams['agentType'];
    const testMode = parsedOutputParams['mode'];
    const modeSetup = agentModelConfig.agentConfig[agentType]?.modes?.[testMode]?.setup;
    const agentSetup = agentModelConfig.agentConfig[agentType]?.setup;
    for (const fieldName of Object.keys(defaultsSetup)) {
      const outputFieldName = defaultsSetup[fieldName].outputField;
      if (!isNil(parsedOutputParams[outputFieldName])) {
        defaultsSetup[fieldName].value = parsedOutputParams[outputFieldName];
      }
    }
    agentModelConfig.agentConfig.default.setup = defaultsSetup;

    // Override the agent type default values with the test config value
    if (agentSetup) {
      for (const fieldName of Object.keys(agentSetup)) {
        const outputFieldName = agentSetup[fieldName].outputField;
        if (!isNil(parsedOutputParams[outputFieldName])) {
          agentSetup[fieldName].value = parsedOutputParams[outputFieldName];
        }
      }
      agentModelConfig.agentConfig[agentType].setup = agentSetup;
    }
    // Override the test modes default values with the test config value
    if (modeSetup) {
      for (const fieldName of Object.keys(modeSetup)) {
        if (!isNil(parsedOutputParams[fieldName])) {
          modeSetup[fieldName].value = parsedOutputParams[fieldName];
        }
      }
      agentModelConfig.agentConfig[agentType].modes[testMode].setup = modeSetup;
    }
    return agentModelConfig;
  }
}
